import { useEffect, useState } from "react";
import { MessageCircle, Trash2, Ban, Loader2, RefreshCw, UserCheck, Send, BadgeCheck, Eraser } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { toast } from "sonner";

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; }
};

function OfficialComposer({ streamId, onSent }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try { await api.post(`/streams/${streamId}/chat`, { text }); setText(""); onSent(); } catch (e2) { toast.error(e2?.response?.data?.detail || "Message not sent"); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="flex gap-2" data-testid="official-chat-form">
      <input data-testid="official-chat-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={300} placeholder="Post to viewers as your church (shows an Official badge)"
        className="flex-1 min-w-0 rounded-lg bg-gray-950 border border-gray-800 px-3 py-2 text-sm text-white placeholder:text-gray-500 outline-none focus:border-amber-500/60 transition-colors" />
      <button data-testid="official-chat-send" type="submit" disabled={busy || !text.trim()} className="h-9 px-3 shrink-0 rounded-lg bg-amber-500 text-black text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 hover:bg-amber-400 transition-colors">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Post
      </button>
    </form>
  );
}

export function ChatModeration({ stream, onToggled }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await api.get(`/streams/${stream.id}/chat`); setData(r.data); } catch { toast.error("Couldn't load chat"); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream.id]);

  const toggle = async (v) => {
    setSaving(true);
    try {
      const r = await api.put(`/streams/${stream.id}`, { chat_enabled: v });
      onToggled(r.data);
      toast.success(v ? "Viewer chat turned on" : "Viewer chat turned off");
      load();
    } catch { toast.error("Couldn't update chat setting"); } finally { setSaving(false); }
  };

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const clearAll = () => {
    if (!window.confirm("Clear all chat messages for everyone? They'll be removed for good.")) return;
    act(() => api.delete(`/streams/${stream.id}/chat`), "Chat cleared");
  };

  const msgs = data?.messages || [];
  const bans = data?.bans || [];

  return (
    <div className="bg-card border border-gray-800 rounded-2xl p-6 space-y-5" data-testid="chat-moderation">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-lg font-semibold flex items-center gap-2"><MessageCircle className="h-5 w-5 text-amber-400" /> Viewer chat</h3>
          <p className="text-sm text-gray-400 mt-1">Viewers type a name to chat beside your live embed. Chat opens automatically while you're live and closes when the broadcast ends. Messages are kept here for review.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {saving && <Loader2 className="h-4 w-4 animate-spin text-amber-400" />}
          <Switch data-testid="chat-enabled-toggle" checked={!!stream.chat_enabled} onCheckedChange={toggle} disabled={saving} className="data-[state=checked]:bg-amber-500" />
        </div>
      </div>

      {stream.chat_enabled && (
        <p data-testid="chat-status" className="text-xs text-gray-400 flex items-center gap-2">
          <span className={"h-1.5 w-1.5 rounded-full " + (data?.open ? "bg-emerald-400" : "bg-gray-500")} />
          {data?.open ? "Chat is open — you're live" : "Chat is closed until you go live"}
        </p>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-white">Messages <span className="text-gray-500 font-normal">({msgs.length})</span></p>
          <div className="flex items-center gap-3">
            <button data-testid="chat-clear-all" onClick={clearAll} disabled={msgs.length === 0} className="text-xs text-gray-400 hover:text-red-400 disabled:opacity-40 disabled:hover:text-gray-400 inline-flex items-center gap-1 transition-colors"><Eraser className="h-3.5 w-3.5" /> Clear chat</button>
            <button data-testid="chat-refresh" onClick={load} className="text-xs text-gray-400 hover:text-amber-400 inline-flex items-center gap-1"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
          </div>
        </div>
        {stream.chat_enabled && <OfficialComposer streamId={stream.id} onSent={load} />}
        <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-800 divide-y divide-gray-800 bg-gray-950">
          {msgs.length === 0 ? (
            <p className="text-sm text-gray-500 p-4 text-center" data-testid="chat-empty">No chat messages yet.</p>
          ) : msgs.map((m) => (
            <div key={m.id} data-testid={`mod-message-${m.id}`} className={"flex items-start gap-3 px-3 py-2.5 " + (m.deleted ? "opacity-40" : "")}>
              <div className="min-w-0 flex-1">
                <p className="text-xs"><span className="font-semibold text-amber-400">{m.name}</span>{m.official && <span data-testid={`mod-official-${m.id}`} className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-amber-500/15 px-1 text-[10px] font-semibold uppercase text-amber-300"><BadgeCheck className="h-3 w-3" /> Official</span>}<span className="text-gray-500 ml-2">{fmt(m.created_at)}</span>{m.deleted && <span className="ml-2 text-red-400">removed</span>}</p>
                <p className="text-sm text-gray-200 break-words">{m.text}</p>
              </div>
              {!m.deleted && (
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`mod-delete-${m.id}`} title="Delete message" onClick={() => act(() => api.delete(`/streams/${stream.id}/chat/${m.id}`), "Message removed")} className="h-8 w-8 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
                  {!m.official && <button data-testid={`mod-ban-${m.id}`} title="Ban viewer" onClick={() => act(() => api.post(`/streams/${stream.id}/chat/bans`, { message_id: m.id }), `${m.name} banned`)} className="h-8 w-8 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center"><Ban className="h-4 w-4" /></button>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {bans.length > 0 && (
        <div className="space-y-2" data-testid="chat-bans">
          <p className="text-sm font-semibold text-white">Banned viewers</p>
          {bans.map((b) => (
            <div key={b.id} className="flex items-center justify-between rounded-lg border border-gray-800 bg-gray-950 px-3 py-2">
              <p className="text-sm text-gray-300">{b.name} <span className="text-xs text-gray-500 ml-2">{fmt(b.created_at)}</span></p>
              <button data-testid={`unban-${b.id}`} onClick={() => act(() => api.delete(`/streams/${stream.id}/chat/bans/${b.id}`), "Viewer unbanned")} className="text-xs text-amber-400 hover:underline inline-flex items-center gap-1"><UserCheck className="h-3.5 w-3.5" /> Unban</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
